import React, { useState, useEffect } from 'react';
import {
  Bell,
  X,
  Calendar,
  Clock,
  MessageSquare,
  Mail,
  RefreshCw,
  Trash2,
  Edit2,
  CheckCircle,
  XCircle,
  AlertCircle,
  Plus
} from 'lucide-react';

const EnhancedReminderPanel = ({ 
  showReminderPanel, 
  setShowReminderPanel, 
  currentUser 
}) => {
  const [activeTab, setActiveTab] = useState('create'); // 'create' or 'view'
  
  // Create reminder states
  const [reminderDate, setReminderDate] = useState('');
  const [reminderTime, setReminderTime] = useState('');
  const [reminderMessage, setReminderMessage] = useState('');
  const [reminderSubmitting, setReminderSubmitting] = useState(false);

  // View reminders states
  const [reminders, setReminders] = useState([]);
  const [remindersLoading, setRemindersLoading] = useState(false);
  const [selectedReminder, setSelectedReminder] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  
  // Edit states
  const [editDate, setEditDate] = useState('');
  const [editTime, setEditTime] = useState('');
  const [editMessage, setEditMessage] = useState('');

  // Fetch reminders when panel opens or tab changes
  useEffect(() => {
    if (showReminderPanel && activeTab === 'view') {
      fetchReminders();
    }
  }, [showReminderPanel, activeTab]);

  const fetchReminders = async () => {
    setRemindersLoading(true);
    try {
      const response = await fetch(
        `https://candi.vdpl.co/Candidates/Candidates/personal_reminders_api.php?user_email=${currentUser?.email}&limit=50`
      );
      const result = await response.json();
      
      if (result.success) {
        setReminders(result.data || []);
      }
    } catch (error) {
      console.error('Error fetching reminders:', error);
    } finally {
      setRemindersLoading(false);
    }
  };

  const handleCreateReminder = async () => {
    if (!reminderDate || !reminderTime || !reminderMessage.trim()) {
      alert('Please fill all reminder fields');
      return;
    }

    setReminderSubmitting(true);
    
    try {
      const reminderDateTime = `${reminderDate} ${reminderTime}`;
      
      const response = await fetch('https://candi.vdpl.co/Candidates/Candidates/personal_reminders_api.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_email: currentUser?.email,
          user_name: `${currentUser?.first_name} ${currentUser?.last_name}`,
          reminder_datetime: reminderDateTime,
          message: reminderMessage,
          created_by: `${currentUser?.first_name} ${currentUser?.last_name}`
        })
      });

      const result = await response.json();

      if (result.success) {
        alert('Reminder set successfully! You will receive an email at the specified time.');
        setReminderDate('');
        setReminderTime('');
        setReminderMessage('');
        setActiveTab('view');
        fetchReminders();
      } else {
        alert('Failed to set reminder: ' + (result.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error setting reminder:', error);
      alert('Error setting reminder. Please try again.');
    } finally {
      setReminderSubmitting(false);
    }
  };

  const handleCancelReminder = async (reminderId) => {
    if (!window.confirm('Are you sure you want to cancel this reminder?')) {
      return;
    }

    try {
      const response = await fetch('https://candi.vdpl.co/Candidates/Candidates/personal_reminders_api.php', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          reminder_id: reminderId,
          user_email: currentUser?.email
        })
      });

      const result = await response.json();

      if (result.success) {
        alert('Reminder cancelled successfully');
        fetchReminders();
      } else {
        alert('Failed to cancel reminder: ' + (result.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error cancelling reminder:', error);
      alert('Error cancelling reminder. Please try again.');
    }
  };

  const handleDeleteReminder = async (reminderId) => {
    if (!window.confirm('Are you sure you want to delete this reminder permanently?')) {
      return;
    }

    try {
      const response = await fetch('https://candi.vdpl.co/Candidates/Candidates/personal_reminders_api.php', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          reminder_id: reminderId,
          user_email: currentUser?.email
        })
      });

      const result = await response.json();

      if (result.success) {
        alert('Reminder deleted successfully');
        fetchReminders();
      } else {
        alert('Failed to delete reminder: ' + (result.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error deleting reminder:', error);
      alert('Error deleting reminder. Please try again.');
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'sent':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
            <CheckCircle className="w-3 h-3" />
            Sent
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
            <Clock className="w-3 h-3" />
            Pending
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
            <XCircle className="w-3 h-3" />
            Failed
          </span>
        );
      case 'cancelled':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
            <XCircle className="w-3 h-3" />
            Cancelled
          </span>
        );
      default:
        return null;
    }
  };

  const formatDateTime = (datetime) => {
    try {
      const date = new Date(datetime);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (e) {
      return datetime;
    }
  };

  if (!showReminderPanel) return null;

  return (
    <>
      <div 
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 animate-fadeIn"
        onClick={() => setShowReminderPanel(false)}
      />
      
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div 
          className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden pointer-events-auto animate-slideUp border-2 border-amber-200 flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-6 rounded-t-2xl relative overflow-hidden flex-shrink-0">
            <div className="absolute inset-0 bg-white/10 backdrop-blur-sm"></div>
            <div className="relative flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <Bell className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="text-xl font-bold text-white">Personal Reminders</h4>
                  <p className="text-xs text-white/90">Manage your scheduled reminders</p>
                </div>
              </div>
              <button
                onClick={() => setShowReminderPanel(false)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Tabs */}
            <div className="relative mt-4 flex gap-2">
              <button
                onClick={() => setActiveTab('create')}
                className={`flex-1 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                  activeTab === 'create'
                    ? 'bg-white text-amber-600 shadow-lg'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                <Plus className="w-4 h-4 inline mr-2" />
                Create Reminder
              </button>
              <button
                onClick={() => setActiveTab('view')}
                className={`flex-1 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                  activeTab === 'view'
                    ? 'bg-white text-amber-600 shadow-lg'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                <Bell className="w-4 h-4 inline mr-2" />
                View Reminders
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {activeTab === 'create' ? (
              // Create Reminder Form
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700 flex items-center">
                    <Calendar className="w-4 h-4 mr-2 text-amber-600" />
                    Reminder Date
                  </label>
                  <input
                    type="date"
                    value={reminderDate}
                    onChange={(e) => setReminderDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm bg-slate-50 hover:bg-white transition-colors"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700 flex items-center">
                    <Clock className="w-4 h-4 mr-2 text-amber-600" />
                    Reminder Time
                  </label>
                  <input
                    type="time"
                    value={reminderTime}
                    onChange={(e) => setReminderTime(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm bg-slate-50 hover:bg-white transition-colors"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700 flex items-center">
                    <MessageSquare className="w-4 h-4 mr-2 text-amber-600" />
                    Reminder Message
                  </label>
                  <textarea
                    value={reminderMessage}
                    onChange={(e) => setReminderMessage(e.target.value)}
                    placeholder="What would you like to be reminded about?"
                    rows="4"
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm resize-none bg-slate-50 hover:bg-white transition-colors"
                    required
                  />
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                  <p className="text-xs text-slate-600 flex items-center">
                    <Mail className="w-3.5 h-3.5 mr-1.5 text-amber-600" />
                    Email will be sent to: <span className="font-semibold ml-1 text-slate-900">{currentUser?.email}</span>
                  </p>
                </div>
              </div>
            ) : (
              // View Reminders List
              <div className="space-y-4">
                {remindersLoading ? (
                  <div className="text-center py-12">
                    <RefreshCw className="w-8 h-8 animate-spin text-amber-500 mx-auto mb-4" />
                    <p className="text-slate-600">Loading reminders...</p>
                  </div>
                ) : reminders.length === 0 ? (
                  <div className="text-center py-12">
                    <Bell className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">No Reminders Found</h3>
                    <p className="text-slate-500 mb-6">You haven't set any reminders yet.</p>
                    <button
                      onClick={() => setActiveTab('create')}
                      className="px-6 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl hover:shadow-lg transition-all font-medium"
                    >
                      Create First Reminder
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {reminders.map((reminder) => (
                      <div 
                        key={reminder.id}
                        className="bg-white border-2 border-slate-200 rounded-xl p-4 hover:border-amber-300 hover:shadow-md transition-all"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              {getStatusBadge(reminder.status)}
                              <span className="text-xs text-slate-500">
                                {formatDateTime(reminder.reminder_datetime)}
                              </span>
                            </div>
                            <p className="text-sm text-slate-700 mb-2 whitespace-pre-wrap">
                              {reminder.message}
                            </p>
                            <div className="flex items-center gap-3 text-xs text-slate-500">
                              <span>Created: {formatDateTime(reminder.created_at)}</span>
                              {reminder.sent_at && (
                                <span>Sent: {formatDateTime(reminder.sent_at)}</span>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {reminder.status === 'pending' && (
                              <button
                                onClick={() => handleCancelReminder(reminder.id)}
                                className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                                title="Cancel Reminder"
                              >
                                <XCircle className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteReminder(reminder.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete Reminder"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          {activeTab === 'create' && (
            <div className="bg-slate-50 p-6 rounded-b-2xl flex items-center justify-end space-x-3 border-t border-slate-200 flex-shrink-0">
              <button
                onClick={() => {
                  setReminderDate('');
                  setReminderTime('');
                  setReminderMessage('');
                  setShowReminderPanel(false);
                }}
                className="px-5 py-2.5 text-slate-600 border-2 border-slate-300 rounded-xl hover:bg-white transition-all text-sm font-medium"
                disabled={reminderSubmitting}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateReminder}
                disabled={reminderSubmitting || !reminderDate || !reminderTime || !reminderMessage.trim()}
                className="px-6 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 shadow-lg hover:shadow-xl"
              >
                {reminderSubmitting ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Setting...</span>
                  </>
                ) : (
                  <>
                    <Bell className="w-4 h-4" />
                    <span>Set Reminder</span>
                  </>
                )}
              </button>
            </div>
          )}

          {activeTab === 'view' && (
            <div className="bg-slate-50 p-4 rounded-b-2xl flex items-center justify-between border-t border-slate-200 flex-shrink-0">
              <p className="text-xs text-slate-600">
                Total: <span className="font-semibold">{reminders.length}</span> reminders
              </p>
              <button
                onClick={fetchReminders}
                disabled={remindersLoading}
                className="px-4 py-2 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors text-sm font-medium flex items-center gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${remindersLoading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default EnhancedReminderPanel;